import { cn } from '@components/shadcn/utils';
import classNames from 'classnames';
import { forwardRef } from 'react';
import styles from './_imageryButton.module.scss';

type ImageryReactButtonProps = Omit<React.HTMLProps<HTMLButtonElement>, 'size' | 'type'>;
type ImageryButtonVariant = 'solid' | 'ghost';
/**
 * The `soft*` types are outlined buttons that tint their background on hover
 * instead of recoloring their border and label. They ignore `variant`.
 */
export type ImageryButtonType =
  | 'primary'
  | 'secondary'
  | 'default'
  | 'danger'
  | 'outline'
  | 'softPrimary'
  | 'softDefault';

interface ImageryButtonProps extends ImageryReactButtonProps {
  rounded?: boolean;
  left?: React.JSX.Element;
  right?: React.JSX.Element;
  variant?: ImageryButtonVariant;
  size?: 'small' | 'medium' | 'large';
  type?: ImageryButtonType;
  block?: boolean;
  dark?: boolean;
}

export const ImageryButton = forwardRef<HTMLButtonElement, ImageryButtonProps>(function Button(
  props,
  ref
) {
  const {
    children,
    size = 'medium',
    variant = 'solid',
    type = 'default',
    left,
    right,
    rounded = false,
    className,
    block,
    dark,
    ...rest
  } = props;

  const sizeClassname = classNames({
    'px-2 py-px': size === 'small',
    'px-3 py-1': size === 'medium',
    'px-4 py-2': size === 'large',
  });

  const variantStyle = classNames({
    [styles.btnSolid]: variant === 'solid',
    [styles.btnGhost]: variant === 'ghost',
  });

  const typeStyle = classNames({
    [styles.btnDefault]: type === 'default',
    [styles.btnDefaultDark]: type === 'default' && dark,
    [styles.btnPrimary]: type === 'primary' && variant === 'solid',
    [styles.btnPrimaryDark]: type === 'primary' && variant === 'solid' && dark,
    [styles.btnPrimaryGhost]: type === 'primary' && variant === 'ghost',
    [styles.btnSecondary]: type === 'secondary',
    [styles.btnDanger]: type === 'danger',
    [styles.btnOutline]: type === 'outline',
    [styles.btnSoftPrimary]: type === 'softPrimary',
    [styles.btnSoftDefault]: type === 'softDefault',
  });

  return (
    <button
      className={cn(
        styles.btn,
        sizeClassname,
        typeStyle,
        variantStyle,
        {
          [styles.btnRounded]: rounded,
          [styles.btnBlock]: block,
        },
        className
      )}
      ref={ref}
      {...rest}
      type="button"
    >
      <div className="flex gap-1 items-center justify-center">
        {left ?? null}
        {children}
        {right ?? null}
      </div>
    </button>
  );
});
